import { createContext, useState, useReducer, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'
import get from 'lodash/get'
import pick from 'lodash/pick'
import toPairs from 'lodash/toPairs'
import { StaticLoader } from 'components/misc/Loader'
import { ExternalLink } from 'components/layout/links'

import set from 'lodash/set'
import toPath from 'lodash/toPath'
import { LocalizedRichText, LocalizedText } from 'localization'

const applyValue = (obj, path, val) => {
  const parts = toPath(path)
  const last = parts.pop()
  if (last) {
    return set(obj, path, val)
  } else {
    const array = get(obj, parts.join('.'), [])
    array.push(val)
    set(obj, parts.join('.'), array)
    return obj
  }
}

export const context = createContext()

export { default as Input } from 'components/form/Input'

const DEFAULT_BASE_ERROR = {
  base: (
    <span>
      <LocalizedRichText
        translationKey="dweb_form_default_base_error"
        params={{
          supportLink: (
            <ExternalLink href={import.meta.env.REACT_APP_SUPPORT_URL}>
              <LocalizedText translationKey="dweb_form_support" />
            </ExternalLink>
          ),
        }}
      />
    </span>
  ),
}

const LOCKED_OUT_BASE_ERROR = {
  base: (
    <span>
      <LocalizedText translationKey="dweb_form_account_permanently_locked_please_contact_us_using_contact" />
    </span>
  ),
}

export function baseError(message, field = 'base') {
  return Promise.reject({ response: { data: { errors: { [field]: message } } } })
}

function fieldsReducer(fields, action) {
  switch (action.type) {
    case 'add': {
      const newField = action.field
      const oldField = fields.find((f) => f.name === newField.name)
      // if (oldField && oldField.changed && oldField.validations !== newField.validations && oldField.error) {
      //   newField.error = newField.validate()
      // } else if (oldField && oldField.error) {
      //   newField.error = oldField.error
      // }
      newField.active = true
      if (oldField) {
        if (oldField.apiError) {
          newField.error = oldField.error
          newField.apiError = oldField.apiError
        } else if (oldField.changed || oldField.error) {
          newField.error = newField.validate()
        }
        newField.changed = oldField.changed
        const newFields = [...fields]
        const index = newFields.findIndex((f) => f === oldField)
        newFields.splice(index, 1, newField)
        return newFields
      } else {
        return [...fields, newField]
      }
    }

    case 'remove': {
      const index = fields.findIndex((f) => f.name === action.name)
      const newFields = [...fields]
      newFields[index].active = false
      return newFields
    }

    case 'change': {
      const index = fields.findIndex((f) => f.name === action.name)
      const field = fields[index]
      const error = fields[index].validate()
      if (!field.changed) {
        // First change, mark as changed
        const newFields = [...fields]
        newFields[index].changed = true
        return newFields
      } else if (field.error && field.error !== error) {
        // Had an error before and this change has changed the error (new error or no longer error)
        const newFields = [...fields]
        newFields[index].error = error
        delete newFields[index].apiError
        return newFields
      } else {
        return fields
      }
    }

    case 'validate_field': {
      const index = fields.findIndex((f) => f.name === action.name)
      const field = fields[index]
      const error = fields[index].validate()
      if (field.error !== error) {
        const newFields = [...fields]
        newFields[index].error = error
        delete newFields[index].apiError
        return newFields
      } else {
        return fields
      }
    }

    case 'validate_all': {
      const newFields = [...fields]
      newFields
        .filter((f) => f.active)
        .forEach((f) => {
          f.error = f.validate(true)
          delete f.apiError
        })
      return newFields
    }

    case 'api_errors': {
      const newFields = [...fields]
      action.errors.forEach(([name, error]) => {
        const index = newFields.findIndex((f) => f.name === name)
        newFields[index].error = error
        newFields[index].apiError = true
      })
      return newFields
    }

    default: {
      console.log('DO NOT KNOW HOW TO REDUCE FIELDS ACTION', action)
      return fields
    }
  }
}

function anyErrors(fields) {
  return !!fields.find((f) => f.active && f.error)
}

function anyChanged(fields) {
  return !!fields.find((f) => f.active && f.changed)
}

function validateFields(fields) {
  return fields.find((f) => f.active && f.validate(true))
}

const Form = forwardRef((props, ref) => {
  const { id, onData, className, children, flashPlacement } = props
  const [loading, setLoading] = useState(false)
  const [fields, fieldsDispatch] = useReducer(fieldsReducer, [])
  const reduxDispatch = useDispatch()

  const getContext = useCallback(
    (name) => {
      return {
        field: fields.find((f) => f.name === name),
        dispatch: fieldsDispatch,
        anyErrors: anyErrors(fields),
        loading: loading,
        formId: id,
      }
    },
    [fields, loading, id]
  )

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault()
      event.stopPropagation()

      if (fields.length === 0) return onData()

      reduxDispatch({ type: 'CLEAR_FLASH', id: 'form-flash' })
      const validationErrors = validateFields(fields)
      if (validationErrors) {
        fieldsDispatch({ type: 'validate_all' })
        reduxDispatch({
          type: 'PUSH_FLASH',
          flash: {
            id: 'form-flash',
            messageKey: 'dweb_flash_fix_errors_before_continuing',
            type: 'error',
            placement: flashPlacement,
          },
        })
      } else if (onData) {
        setLoading(true)
        let data
        try {
          data = await fields
            .filter((f) => f.active)
            .reduce(async (data, field) => {
              data = await data
              const value = await field.getValue()
              applyValue(data, field.name, value === undefined ? '' : value)
              return data
            }, {})
        } catch (err) {
          reduxDispatch({ type: 'PUSH_FLASH', flash: { id: 'form-flash', message: err.message || err, type: 'error' } })
          setLoading(false)
          return
        }
        onData(data)
          .catch((error) => {
            // console.log('errors start', error)
            const fallbackBaseError = error.response.status === 403 ? LOCKED_OUT_BASE_ERROR : DEFAULT_BASE_ERROR
            let errors = get(error, 'response.data.errors') || fallbackBaseError
            if (errors) {
              if (typeof errors === 'string') {
                errors = { base: errors }
              } else if (Array.isArray(errors)) {
                errors = { base: errors[0] }
              } else if (!Object.keys(errors).length) {
                errors = fallbackBaseError
              }
              // Only include errors for fields that were submitted.
              const fieldErrors = toPairs(pick(errors, Object.keys(data)))
              // console.log({ errors, fieldErrors, fields })
              if (errors.base) {
                reduxDispatch({
                  type: 'PUSH_FLASH',
                  flash: { id: 'form-flash', message: errors.base, type: 'error', placement: flashPlacement },
                })
              } else if (fieldErrors.length) {
                fieldsDispatch({ type: 'api_errors', errors: fieldErrors })
              } else if (Object.keys(errors).length && Object.keys(errors)[0] !== 'base') {
                const key = Object.keys(errors)[0]
                reduxDispatch({
                  type: 'PUSH_FLASH',
                  flash: { id: 'form-flash', message: errors[key][0], type: 'error', placement: flashPlacement },
                })
              }
            }
          })
          .finally(() => setLoading(false))
      } else {
        // console.log('No props.onData set on Form')
      }
    },
    [fields, onData, reduxDispatch, flashPlacement]
  )

  return (
    <context.Provider value={getContext}>
      <form ref={ref} id={id} className={className} onSubmit={handleSubmit} data-changed={anyChanged(fields)}>
        {children}
        {loading && <StaticLoader />}
      </form>
    </context.Provider>
  )
})

export default Form
